"use client";

import * as React from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import {
  CalloutNode,
  LongFormImageNode,
  RICH_TEXT_CALLOUT_TONES,
  type RichTextCalloutTone,
} from "./rich-text-long-form";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Mention from "@tiptap/extension-mention";
import {
  BoldIcon,
  ChevronDownIcon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  RemoveFormattingIcon,
  UnderlineIcon,
} from "lucide-react";

import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { createMentionSuggestion, type MentionQuery } from "./mention-list";
import { cn } from "../../lib/utils";
import { useIsMobile } from "../../lib/use-mobile";

export type { MentionItem, MentionQuery } from "./mention-list";

/**
 * App-agnostic rich-text editor + read-only renderer + reveal-on-click composer.
 *
 * The editor is intentionally constrained to a small basic-text subset (bold,
 * italic, underline, link). The client output is NEVER trusted — every persisted
 * value must be sanitized on the backend before storage and is rendered here only
 * after that server-side sanitization (see {@link RichTextContent}).
 *
 * Labels are injected (not i18n-coupled) so the component stays free of any
 * specific app's translation system; pass translated strings via `labels`.
 */

export type RichTextEditorLabels = {
  bold: string;
  italic: string;
  underline: string;
  bulletList: string;
  orderedList: string;
  headings: string;
  paragraph: string;
  heading1: string;
  heading2: string;
  heading3: string;
  link: string;
  clearFormatting: string;
  linkPrompt: string;
  linkApply: string;
  linkRemove: string;
  /** Long-form only. Optional so every existing caller keeps compiling. */
  blockquote?: string;
  codeBlock?: string;
  horizontalRule?: string;
  heading4?: string;
  image?: string;
  imagePrompt?: string;
  callout?: string;
  calloutNote?: string;
  calloutWarning?: string;
  calloutTip?: string;
};

export const DEFAULT_RICH_TEXT_EDITOR_LABELS: RichTextEditorLabels = {
  bold: "Bold",
  italic: "Italic",
  underline: "Underline",
  bulletList: "Bulleted list",
  orderedList: "Numbered list",
  headings: "Text style",
  paragraph: "Normal text",
  heading1: "Heading",
  heading2: "Subheading",
  heading3: "Small heading",
  link: "Link",
  clearFormatting: "Clear formatting",
  linkPrompt: "Enter a URL",
  linkApply: "Apply",
  linkRemove: "Remove",
  blockquote: "Quote",
  codeBlock: "Code block",
  horizontalRule: "Separator",
  heading4: "Smallest heading",
  image: "Image",
  imagePrompt: "Image URL",
  callout: "Callout",
  calloutNote: "Note",
  calloutWarning: "Warning",
  calloutTip: "Tip",
};

/**
 * Shared element styling so formatting renders consistently WITHOUT depending on
 * the Tailwind typography (`prose`) plugin being present in the host app.
 */
export const RICH_TEXT_ELEMENT_CLASSES =
  "[&_p]:my-0 [&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_a]:text-primary [&_a]:underline " +
  // Lists render with markers + indentation without relying on the Tailwind typography (`prose`) plugin.
  "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 " +
  // Headings (levels 1–3) — sized down for inline notes/descriptions.
  "[&_h1]:mt-3 [&_h1]:mb-1 [&_h1]:text-xl [&_h1]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_h3]:text-base [&_h3]:font-semibold " +
  // Mention chips (data-type=mention) — a primary-tinted inline pill. Inert: just styling, no behavior.
  "[&_[data-type=mention]]:rounded [&_[data-type=mention]]:bg-primary/10 [&_[data-type=mention]]:px-1 [&_[data-type=mention]]:py-0.5 [&_[data-type=mention]]:font-medium [&_[data-type=mention]]:text-primary";

const EMPTY_HTML = "<p></p>";

/**
 * The mention node, persisted as `<span data-type="mention" data-id data-label>@Label</span>` — exactly the inert
 * shape the server sanitizer allowlists. `id`/`label` round-trip via the data attributes.
 */
const MentionNode = Mention.extend({
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-id"),
        renderHTML: (attrs: { id?: string | null }) => (attrs.id ? { "data-id": attrs.id } : {}),
      },
      label: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-label"),
        renderHTML: (attrs: { label?: string | null }) => (attrs.label ? { "data-label": attrs.label } : {}),
      },
    };
  },
});

export function isEmptyRichTextHtml(html: string) {
  const stripped = html.replace(/<[^>]*>/g, "").trim();
  return stripped.length === 0;
}

type RichTextEditorProps = {
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  /** Focus the editor as soon as it mounts (e.g. right after a reveal-on-click composer expands). */
  autoFocus?: boolean;
  labels?: Partial<RichTextEditorLabels>;
  className?: string;
  /** Styles the actual editable element (e.g. `min-h-[60vh]` so the whole area is clickable/typeable, Notion-style). */
  contentClassName?: string;
  /**
   * `toolbar` (default): a bordered box with a fixed toolbar on top.
   * `bubble`: borderless inline text (the caller styles it via `className`); the formatting controls appear as a
   * floating menu below the selection. Use for inline-editable headings/paragraphs.
   */
  variant?: "toolbar" | "bubble";
  /**
   * Long-form article mode: adds quotes, code blocks, separators, h4, images and callouts.
   *
   * OFF by default and additive on purpose. Announcements and budget notes use this same component, and
   * their toolbar must not grow controls for a format their sanitizer profile would strip on save.
   */
  longForm?: boolean;
  /** Long-form only: called when the author asks for an image. Resolve to a URL, or null to cancel. */
  onRequestImage?: () => Promise<string | null>;
  /**
   * Enable @-mentions (INTERNAL editors only — notes/comments, never public/shared content). `query` searches the
   * people that can be mentioned for the typed text. Omit on any editor whose content is shown publicly.
   */
  mentions?: { query: MentionQuery };
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
};

/** The formatting controls, shared by the fixed toolbar and the selection bubble menu. */
function FormattingControls({
  editor,
  disabled,
  labels,
  longForm = false,
  onRequestImage,
}: {
  editor: Editor;
  disabled?: boolean;
  labels: RichTextEditorLabels;
  longForm?: boolean;
  onRequestImage?: () => Promise<string | null>;
}) {
  return (
    <>
      <ToolbarButton
        active={editor.isActive("bold")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBold().run()}
        ariaLabel={labels.bold}
      >
        <BoldIcon />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive("italic")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        ariaLabel={labels.italic}
      >
        <ItalicIcon />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive("underline")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        ariaLabel={labels.underline}
      >
        <UnderlineIcon />
      </ToolbarButton>
      <LinkPopover editor={editor} disabled={disabled} labels={labels} />
      <ToolbarButton
        disabled={disabled}
        onClick={() => editor.chain().focus().unsetAllMarks().run()}
        ariaLabel={labels.clearFormatting}
      >
        <RemoveFormattingIcon />
      </ToolbarButton>
      {longForm ? (
        <LongFormControls
          editor={editor}
          disabled={disabled}
          labels={labels}
          onRequestImage={onRequestImage}
        />
      ) : null}
    </>
  );
}

/**
 * Quote, code, separator, image and the three callout tones.
 *
 * Every control here maps to a tag on the server's LONG-FORM sanitizer profile. Adding one without adding it
 * there means an author uses it, saves, and watches it disappear.
 */
function LongFormControls({
  editor,
  disabled,
  labels,
  onRequestImage,
}: {
  editor: Editor;
  disabled?: boolean;
  labels: RichTextEditorLabels;
  onRequestImage?: () => Promise<string | null>;
}) {
  const toneLabel: Record<RichTextCalloutTone, string> = {
    note: labels.calloutNote ?? "Note",
    warning: labels.calloutWarning ?? "Warning",
    tip: labels.calloutTip ?? "Tip",
  };

  return (
    <>
      <ToolbarButton
        active={editor.isActive("blockquote")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        ariaLabel={labels.blockquote ?? "Quote"}
      >
        <span aria-hidden className='text-sm font-semibold'>&ldquo;</span>
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive("codeBlock")}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        ariaLabel={labels.codeBlock ?? "Code block"}
      >
        <span aria-hidden className='font-mono text-xs'>{"</>"}</span>
      </ToolbarButton>
      <ToolbarButton
        disabled={disabled}
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        ariaLabel={labels.horizontalRule ?? "Separator"}
      >
        <span aria-hidden className='text-sm'>&mdash;</span>
      </ToolbarButton>
      {onRequestImage ? (
        <ToolbarButton
          disabled={disabled}
          onClick={() => {
            void onRequestImage().then((src) => {
              // Null means the author cancelled, or the upload failed and already said so.
              if (src) editor.chain().focus().setLongFormImage({ src }).run();
            });
          }}
          ariaLabel={labels.image ?? "Image"}
        >
          <span aria-hidden className='text-sm'>&#9634;</span>
        </ToolbarButton>
      ) : null}
      {RICH_TEXT_CALLOUT_TONES.map((tone) => (
        <ToolbarButton
          key={tone}
          active={editor.isActive("callout", { tone })}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleCallout(tone).run()}
          ariaLabel={`${labels.callout ?? "Callout"}: ${toneLabel[tone]}`}
        >
          <span aria-hidden className='text-[10px] font-semibold uppercase'>{toneLabel[tone].slice(0, 1)}</span>
        </ToolbarButton>
      ))}
    </>
  );
}

function ToolbarButton({
  active,
  disabled,
  onClick,
  ariaLabel,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      type='button'
      variant={active ? "default" : "ghost"}
      size='icon-sm'
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={ariaLabel}
      aria-pressed={active ?? false}
      className='size-7'
    >
      {children}
    </Button>
  );
}

function HeadingDropdown({
  editor,
  disabled,
  labels,
}: {
  editor: Editor;
  disabled?: boolean;
  labels: RichTextEditorLabels;
}) {
  // The trigger shows a compact, language-agnostic code (the menu keeps the full readable labels) so the control
  // stays narrow inside tight fields like inline notes.
  const activeShort =
    editor.isActive("heading", { level: 1 }) ? "H1"
    : editor.isActive("heading", { level: 2 }) ? "H2"
    : editor.isActive("heading", { level: 3 }) ? "H3"
    : "P";

  const items: Array<{ key: string; label: string; isActive: boolean; apply: () => void }> = [
    {
      key: "p",
      label: labels.paragraph,
      isActive: editor.isActive("paragraph"),
      apply: () => editor.chain().focus().setParagraph().run(),
    },
    ...([1, 2, 3] as const).map((level) => ({
      key: `h${level}`,
      label: level === 1 ? labels.heading1 : level === 2 ? labels.heading2 : labels.heading3,
      isActive: editor.isActive("heading", { level }),
      apply: () => editor.chain().focus().toggleHeading({ level }).run(),
    })),
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          disabled={disabled}
          aria-label={labels.headings}
          title={labels.headings}
          className='h-7 gap-0.5 px-1.5 text-xs font-medium'
        >
          {activeShort}
          <ChevronDownIcon className='size-3.5' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start' className='min-w-36'>
        {items.map((item) => (
          <DropdownMenuItem
            key={item.key}
            onSelect={(event) => {
              event.preventDefault();
              item.apply();
            }}
            className={cn(item.isActive && "bg-accent text-accent-foreground")}
          >
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LinkPopover({
  editor,
  disabled,
  labels,
}: {
  editor: Editor;
  disabled?: boolean;
  labels: RichTextEditorLabels;
}) {
  const [open, setOpen] = React.useState(false);
  const [href, setHref] = React.useState("");

  React.useEffect(() => {
    if (open) {
      const previousHref = (editor.getAttributes("link")?.href as string | undefined) ?? "";
      setHref(previousHref);
    }
  }, [editor, open]);

  const isActive = editor.isActive("link");

  const handleApply = () => {
    const trimmed = href.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: trimmed, target: "_blank", rel: "noopener noreferrer" })
        .run();
    }
    setOpen(false);
  };

  const handleRemove = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant={isActive ? "default" : "ghost"}
          size='icon-sm'
          aria-label={labels.link}
          title={labels.link}
          aria-pressed={isActive}
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          className='size-7'
        >
          <LinkIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-72 space-y-2 p-3'>
        <p className='text-muted-foreground text-xs font-medium'>{labels.linkPrompt}</p>
        <Input
          autoFocus
          value={href}
          onChange={(event) => setHref(event.target.value)}
          placeholder='https://example.com'
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleApply();
            }
          }}
        />
        <div className='flex justify-end gap-2'>
          {isActive ? (
            <Button type='button' variant='outline' size='sm' onClick={handleRemove}>
              {labels.linkRemove}
            </Button>
          ) : null}
          <Button type='button' size='sm' onClick={handleApply}>
            {labels.linkApply}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function RichTextEditor({
  value,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  autoFocus = false,
  labels: labelsProp,
  className,
  contentClassName,
  variant = "toolbar",
  longForm = false,
  onRequestImage,
  mentions,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: RichTextEditorProps) {
  const labels = React.useMemo(
    () => ({ ...DEFAULT_RICH_TEXT_EDITOR_LABELS, ...labelsProp }),
    [labelsProp],
  );
  const isBubble = variant === "bubble";

  // Keep the latest query without recreating the editor (the editor is created once; the host may pass a fresh
  // query closure each render).
  const mentionQueryRef = React.useRef<MentionQuery | undefined>(mentions?.query);
  mentionQueryRef.current = mentions?.query;
  const mentionsEnabled = Boolean(mentions);

  // Strict allow-list of marks/nodes. StarterKit ships many extensions we
  // explicitly disable to keep the format to basic text.
  const editor = useEditor({
    autofocus: autoFocus ? "end" : false,
    extensions: [
      StarterKit.configure({
        // h4 only in long form: an article's body needs a fourth level, a banner does not.
        heading: { levels: longForm ? [1, 2, 3, 4] : [1, 2, 3] },
        // Each of these is enabled ONLY in long form, and each is on the server's long-form allowlist.
        // Enabling one here without adding it there would strip it silently on save.
        blockquote: longForm ? undefined : false,
        codeBlock: longForm ? undefined : false,
        code: longForm ? undefined : false,
        horizontalRule: longForm ? undefined : false,
        strike: false,
        // Keep paragraph + hardBreak + history + bold + italic + bullet/ordered lists + headings enabled.
      }),
      ...(longForm ? [CalloutNode, LongFormImageNode] : []),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: false,
        protocols: ["https", "http", "mailto"],
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      // @-mentions: only when the host opts in (internal editors). Public editors never pass `mentions`.
      ...(mentionsEnabled
        ? [
            MentionNode.configure({
              HTMLAttributes: { "data-type": "mention" },
              suggestion: createMentionSuggestion((text) => mentionQueryRef.current?.(text) ?? Promise.resolve([])),
            }),
          ]
        : []),
    ],
    content: value && !isEmptyRichTextHtml(value) ? value : EMPTY_HTML,
    editable: !disabled,
    editorProps: {
      attributes: {
        class: cn(
          // Bubble: borderless inline text (the caller styles size/weight via `className`). Toolbar: a boxed area.
          isBubble
            ? "outline-none"
            : "min-h-24 max-h-64 overflow-y-auto rounded-b-lg bg-transparent px-3 py-2 text-sm leading-relaxed text-input-foreground outline-none",
          // `min-w-0` lets the inner ProseMirror shrink inside flex/grid
          // parents; `break-words` + `whitespace-pre-wrap` ensure long,
          // unbroken strings (URLs, server tokens, etc.) wrap instead of
          // overflowing the container.
          "min-w-0 break-words whitespace-pre-wrap",
          "max-w-none",
          RICH_TEXT_ELEMENT_CLASSES,
          contentClassName,
        ),
        ...(placeholder ? { "data-placeholder": placeholder } : {}),
        ...(ariaInvalid ? { "aria-invalid": "true" } : {}),
        ...(ariaDescribedBy ? { "aria-describedby": ariaDescribedBy } : {}),
      },
    },
    onUpdate: ({ editor: instance }) => {
      const html = instance.getHTML();
      onChange(isEmptyRichTextHtml(html) ? "" : html);
    },
    onBlur: () => {
      onBlur?.();
    },
  });

  // Keep the editor content in sync when the bound value changes externally
  // (form resets, edit/duplicate flows).
  React.useEffect(() => {
    // A tree hidden by Suspense and shown again (a lazy form inside a dialog) re-runs this effect against the
    // instance that was destroyed while hidden; skip it until the fresh one arrives.
    if (!editor || editor.isDestroyed) return;
    const current = editor.getHTML();
    const normalizedCurrent = isEmptyRichTextHtml(current) ? "" : current;
    const normalizedNext = value && !isEmptyRichTextHtml(value) ? value : "";
    if (normalizedCurrent !== normalizedNext) {
      editor.commands.setContent(normalizedNext.length > 0 ? value : EMPTY_HTML, { emitUpdate: false });
    }
  }, [editor, value]);

  React.useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (editor.isEditable === disabled) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) {
    return isBubble ? (
      <div className={className} aria-busy='true' />
    ) : (
      <div className='border-border bg-muted/30 min-h-32 rounded-md border' aria-busy='true' />
    );
  }

  // Bubble variant — inline editable text with a selection menu that appears below the selection.
  if (isBubble) {
    return (
      <div className={cn("w-full min-w-0", disabled && "opacity-60", className)}>
        {!disabled ? (
          <BubbleMenu
            editor={editor}
            options={{ placement: "bottom" }}
            className='bg-popover text-popover-foreground z-50 flex items-center gap-1 rounded-lg border p-1 shadow-[var(--shadow-popover)]'
          >
            <FormattingControls editor={editor} disabled={disabled} labels={labels} />
          </BubbleMenu>
        ) : null}
        <EditorContent editor={editor} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        // `w-full min-w-0` + `overflow-hidden` makes the wrapper respect its
        // grid/flex parent's width so a long line in the content cannot push
        // the container past its max-width.
        // Match the Input/Textarea chrome: rounded-lg, solid bg-background (dark:bg-input), subtle shadow,
        // border-border, and the same focus ring.
        "border-border bg-background dark:bg-input shadow-[var(--shadow-field)] focus-within:border-ring focus-within:ring-ring/50 w-full min-w-0 overflow-hidden rounded-lg border transition-colors focus-within:ring-3",
        ariaInvalid && "border-destructive ring-destructive/30",
        disabled && "opacity-60",
        className,
      )}
    >
      <div className='border-border/60 flex flex-wrap items-center gap-1 border-b px-2 py-1'>
        <HeadingDropdown editor={editor} disabled={disabled} labels={labels} />
        <ToolbarButton
          active={editor.isActive("bold")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
          ariaLabel={labels.bold}
        >
          <BoldIcon />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("italic")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          ariaLabel={labels.italic}
        >
          <ItalicIcon />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("underline")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          ariaLabel={labels.underline}
        >
          <UnderlineIcon />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("bulletList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          ariaLabel={labels.bulletList}
        >
          <ListIcon />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("orderedList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          ariaLabel={labels.orderedList}
        >
          <ListOrderedIcon />
        </ToolbarButton>
        <LinkPopover editor={editor} disabled={disabled} labels={labels} />
        <ToolbarButton
          disabled={disabled}
          onClick={() => editor.chain().focus().unsetAllMarks().run()}
          ariaLabel={labels.clearFormatting}
        >
          <RemoveFormattingIcon />
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

type RichTextContentProps = {
  /** Server-sanitized HTML. The caller MUST guarantee this was sanitized on the backend. */
  html: string;
  className?: string;
};

/**
 * Read-only renderer for already-server-sanitized rich-text HTML. Uses the same
 * explicit element styling as the editor so output matches the authoring view.
 */
export function RichTextContent({ html, className }: RichTextContentProps) {
  return (
    <div
      className={cn(
        "min-w-0 text-sm leading-relaxed break-words [&_p:not(:last-child)]:mb-2",
        RICH_TEXT_ELEMENT_CLASSES,
        className,
      )}
      // Safe: `html` is sanitized on the backend before persistence.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

type RichTextComposerProps = {
  /** Submit handler. May be async; the composer clears + collapses on resolve. */
  onSubmit: (html: string) => void | Promise<void>;
  placeholder: string;
  submitLabel: string;
  cancelLabel: string;
  /** Toolbar labels for the underlying editor. */
  labels?: Partial<RichTextEditorLabels>;
  pending?: boolean;
  disabled?: boolean;
  /** Optional leading icon for the submit button. */
  submitIcon?: React.ReactNode;
  /**
   * When `true` (default) the composer starts collapsed as a placeholder button
   * that reveals the editor on click. When `false` the editor is shown directly
   * (always expanded) and Cancel only clears the draft.
   */
  revealOnClick?: boolean;
  /** Enable @-mentions in the composer (forwarded to the editor; internal editors only). */
  mentions?: { query: MentionQuery };
  className?: string;
};

/**
 * Composer around {@link RichTextEditor} with Save / Cancel. Manages its own
 * draft and clears after a successful submit. By default it starts collapsed as
 * a placeholder button that reveals the editor on click (`revealOnClick`); set
 * `revealOnClick={false}` to show the editor directly. Reusable anywhere a
 * "click to add a note/comment" affordance is needed.
 */
export function RichTextComposer({
  onSubmit,
  placeholder,
  submitLabel,
  cancelLabel,
  labels,
  pending = false,
  disabled = false,
  submitIcon,
  revealOnClick = true,
  mentions,
  className,
}: RichTextComposerProps) {
  const [expanded, setExpanded] = React.useState(!revealOnClick);
  const [draft, setDraft] = React.useState("");
  const isMobile = useIsMobile();

  // Clear the draft; in reveal mode also collapse back to the placeholder button.
  const reset = () => {
    setDraft("");
    if (revealOnClick) setExpanded(false);
  };

  const handleSubmit = async () => {
    const body = draft.trim();
    if (!body || pending || disabled) return;
    try {
      await onSubmit(draft);
      reset();
    } catch {
      // Keep the draft + expanded state so the user can retry on failure.
    }
  };

  if (revealOnClick && !expanded) {
    return (
      <button
        type='button'
        disabled={disabled}
        onClick={() => setExpanded(true)}
        className={cn(
          isMobile
            ? // Phone: a white row on the canvas, like the app's other phone rows.
              "flex min-h-[3.25rem] w-full cursor-text items-center rounded-2xl bg-card px-4 text-left text-base font-semibold text-muted-foreground/80 active:bg-muted disabled:opacity-60"
            : "border-input bg-input/30 text-muted-foreground hover:border-ring/60 flex min-h-11 w-full cursor-text items-center rounded-md border px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60",
          className,
        )}
      >
        {placeholder}
      </button>
    );
  }

  if (isMobile) {
    // Phone: the editor on a white card, then Cancel / Save as two full-width pills (the phone sheets' buttons).
    return (
      <div className={cn("flex flex-col gap-2.5", className)}>
        <div className='overflow-hidden rounded-2xl bg-card'>
          <RichTextEditor
            value={draft}
            onChange={setDraft}
            placeholder={placeholder}
            disabled={disabled || pending}
            autoFocus={revealOnClick}
            labels={labels}
            mentions={mentions}
          />
        </div>
        <div className='grid grid-cols-2 gap-2.5'>
          <button
            type='button'
            onClick={reset}
            disabled={pending}
            className='h-11 rounded-full bg-card text-[15px] font-extrabold text-foreground ring-1 ring-border/70 active:bg-muted disabled:opacity-60'
          >
            {cancelLabel}
          </button>
          <button
            type='button'
            onClick={() => void handleSubmit()}
            disabled={!draft.trim() || pending || disabled}
            className='flex h-11 items-center justify-center gap-2 rounded-full bg-primary text-[15px] font-extrabold text-primary-foreground active:brightness-95 disabled:opacity-45'
          >
            {submitIcon}
            {submitLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      <RichTextEditor
        value={draft}
        onChange={setDraft}
        placeholder={placeholder}
        disabled={disabled || pending}
        autoFocus={revealOnClick}
        labels={labels}
        mentions={mentions}
      />
      <div className='flex justify-end gap-2'>
        <Button variant='outline' size='sm' onClick={reset} disabled={pending}>
          {cancelLabel}
        </Button>
        <Button size='sm' onClick={() => void handleSubmit()} disabled={!draft.trim() || pending || disabled}>
          {submitIcon}
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
